import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ListCard, ListTable, PHARMACY_PAGE_SIZE } from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { getPharmacyNearExpiryListApi } from "../../../services/pharmacyDashboard.services";
import { formatAmount } from "../common/money";

const { RangePicker } = DatePicker;

/*
 * Rows here are batch records, so the medicine-level fields may arrive either
 * bare or prefixed by the serializer. Read both rather than assume one.
 */
const medicineField = (record, field) =>
  record?.[field] ?? record?.[`pharmacy_medicine_${field}`];

const columns = [
  { title: "Medicine", dataIndex: "pharmacy_medicine_name", key: "pharmacy_medicine_name" },
  { title: "Batch Number", dataIndex: "batch_number", key: "batch_number" },
  { title: "Expiry", dataIndex: "expiry", key: "expiry" },
  { title: "Available Qty", dataIndex: "available_qty", key: "available_qty" },
  {
    title: "Current Stock",
    key: "current_stock",
    render: (_, record) => medicineField(record, "current_stock") ?? "-",
  },
  {
    title: "Packing",
    key: "packing",
    render: (_, record) => medicineField(record, "packing") ?? "-",
  },
  {
    title: "Rate",
    key: "rate_a",
    align: "right",
    render: (_, record) => formatAmount(medicineField(record, "rate_a") || 0),
  },
  {
    title: "Value",
    key: "value",
    align: "right",
    // Valued on the quantity in this batch - that is what is at risk of
    // expiring. A negative quantity values at zero rather than going negative.
    render: (_, record) =>
      formatAmount(
        Number(medicineField(record, "rate_a") || 0) *
          Math.max(0, Number(record?.available_qty || 0))
      ),
  },
];

const NearExpiryTable = ({ onLoaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [range, setRange] = useState(null);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (range?.[0] && range?.[1]) {
      queryParams.from = range[0].format("YYYY-MM-DD");
      queryParams.to = range[1].format("YYYY-MM-DD");
    }
    const queryString = new URLSearchParams(queryParams);
    getPharmacyNearExpiryListApi(queryString)
      .then((res) => {
        setData(res?.data);
        onLoaded?.(res?.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, range]);

  const total = Number(data?.count) || 0;

  return (
    <ListCard
      title="Near Expiry"
      range={{ page, pageSize: PHARMACY_PAGE_SIZE, total: total }}
      count={total}
      toolbar={
        <RangePicker
          onChange={(value) => {
            setRange(value);
            setPage(1);
          }}
          defaultValue={[dayjs(), dayjs().add(90, "day")]}
        />
      }
    >
      <ListTable
        loading={loading}
        dataSource={data?.results || []}
        columns={columns}
        rowKey="id"
        pagination={getTablePagination({
          current: page,
          pageSize: PHARMACY_PAGE_SIZE,
          total,
          onChange: (p) => setPage(p),
        })}
      />
    </ListCard>
  );
};

export default NearExpiryTable;
